import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Trans, useTranslation } from 'react-i18next';
import { BillingPeriod } from '../../../models/functions/billing-period';
import { cn } from '../../../utils/tailwind';
import { getPriceComparison } from './pricing-data';

const PriceNumber: React.FC<{
    value: number;
    format: (value: number) => string;
    fromZero?: boolean;
    startFrom?: number;
    onValue?: (value: number) => void;
    reducedMotion: boolean;
}> = ({ value, format, fromZero = false, startFrom, onValue, reducedMotion }) => {
    const current = React.useRef(fromZero ? 0 : value);
    const [displayed, setDisplayed] = React.useState(current.current);
    const onValueRef = React.useRef(onValue);
    onValueRef.current = onValue;

    React.useEffect(() => {
        if (reducedMotion) {
            current.current = value;
            setDisplayed(value);
            onValueRef.current?.(value);
            return undefined;
        }
        const from = startFrom ?? current.current;
        current.current = from;
        setDisplayed(from);
        if (from === value) return undefined;
        const start = performance.now();
        let frame = 0;
        const tick = (now: number) => {
            const progress = Math.min((now - start) / 650, 1);
            const eased = 1 - (1 - progress) ** 3;
            current.current = progress === 1 ? value : from + (value - from) * eased;
            setDisplayed(current.current);
            onValueRef.current?.(current.current);
            if (progress < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [value, startFrom, reducedMotion]);

    const animatedValue = Number.isInteger(value) ? Math.round(displayed) : displayed;
    const visibleValue = reducedMotion ? value : animatedValue;

    return (
        <span className="relative inline-block tabular-nums">
            <span className="invisible">{format(value)}</span>
            <span className="absolute inset-0">{format(visibleValue)}</span>
        </span>
    );
};

export const HomepagePricingPrice: React.FC<{ period: BillingPeriod; withVat: boolean }> = ({ period, withVat }) => {
    const { t, i18n } = useTranslation();
    const reducedMotion = !!useReducedMotion();
    const mounted = React.useRef(false);
    React.useEffect(() => {
        mounted.current = true;
    }, []);
    const comparison = getPriceComparison(period, withVat);
    const discounted = comparison.savings > 0;
    const wasDiscounted = React.useRef(discounted);
    const discountedValue = React.useRef(comparison.monthlyEquivalent);
    const restoreFrom = !discounted && wasDiscounted.current ? discountedValue.current : undefined;
    React.useEffect(() => {
        wasDiscounted.current = discounted;
    }, [discounted]);
    const formatPrice = (value: number) =>
        new Intl.NumberFormat(i18n.language === 'en' ? 'en-US' : 'cs-CZ', {
            style: 'currency',
            currency: 'CZK',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(value);
    const transition = { duration: reducedMotion ? 0 : 0.5, ease: [0.22, 1, 0.36, 1] };

    return (
        <div>
            <p className="sr-only">
                {discounted &&
                    `${t('pages.homepage.pricingDesign.monthlyPrice')} ${formatPrice(comparison.monthlyPrice)}. `}
                {formatPrice(comparison.monthlyEquivalent)} {t('pages.homepage.pricingDesign.perMonth')}
            </p>
            <div aria-hidden="true" className="flex flex-wrap items-baseline gap-x-[1.2rem] gap-y-[0.4rem]">
                <motion.span
                    layout={!reducedMotion}
                    transition={transition}
                    className={cn(
                        'relative inline-block whitespace-nowrap',
                        discounted
                            ? 'text-[1.6rem] text-web-copy'
                            : 'text-[3.4rem] font-bold leading-tight tracking-tight text-brand-secondary sm:text-[4.4rem]',
                    )}
                >
                    <PriceNumber
                        value={comparison.monthlyPrice}
                        format={formatPrice}
                        startFrom={restoreFrom}
                        reducedMotion={reducedMotion}
                    />
                    <motion.span
                        initial={false}
                        animate={{ scaleX: discounted ? 1 : 0 }}
                        transition={{ duration: reducedMotion ? 0 : 0.3 }}
                        className="pointer-events-none absolute inset-x-0 top-1/2 h-px origin-left bg-current"
                    />
                </motion.span>
                {discounted && (
                    <motion.span
                        layout={!reducedMotion}
                        initial={mounted.current && !reducedMotion ? { opacity: 0, y: 12 } : false}
                        animate={{ opacity: 1, y: 0 }}
                        transition={transition}
                        className="inline-block whitespace-nowrap text-[3.4rem] font-bold leading-tight tracking-tight text-[#e65c00] sm:text-[4.4rem]"
                    >
                        <PriceNumber
                            value={comparison.monthlyEquivalent}
                            format={formatPrice}
                            fromZero={mounted.current}
                            onValue={(value) => {
                                discountedValue.current = value;
                            }}
                            reducedMotion={reducedMotion}
                        />
                    </motion.span>
                )}
                <motion.span
                    layout="position"
                    transition={transition}
                    className="whitespace-nowrap text-[1.6rem] text-web-copy"
                >
                    {t('pages.homepage.pricingDesign.perMonth')}
                </motion.span>
            </div>
            <p className="mt-[1rem] text-[1.4rem] leading-relaxed text-web-copy">
                <Trans
                    i18nKey={`pages.homepage.pricingDesign.billing.${period}`}
                    values={{ amount: formatPrice(comparison.total) }}
                    components={{ strong: <span /> }}
                />
            </p>
        </div>
    );
};
